"use client";

import React from "react";
import { useLowRegisterDecimal } from "../../hooks/useLowRegisterDecimal";
import { formatRepeatingZeroDecimal } from "../../hooks/formatRepeatingZeroDecimal";
import { type FC, useCallback, type ChangeEvent, useEffect, useState } from "react";
import { Content } from "./components";
import Card from "@components/shared/card/Card";
import Separator from "@/components/ui/separator";
import AddressInput from "./components/AddressInput";
import { createModalHelpers, useModalStore } from "@stores/unified-modal.store";
import { useMemeSwap } from "@stores/meme-swap.store";
import { useMemeSwapListStore } from "@/stores/meme-swap-list.store";
import { usePools } from "@/stores/pools.store";
import Currencies from "@components/shared/modals/Currencies";
import CurrencyForm from "../Swap/components/CurrencyForm";
import { useWallet } from "@/stores/wallet.store";
import BigSwitch from "@/components/ui/BigSwitch";
import CardHeader from "./header";
import { useSnipesStore } from "@stores/snipes.store";
import MemeSwapButton from "./components/MemeSwapButton";
import SwapDetails from "../MemeSwap/components/memeSwap-Details";
import { WalletController } from "@/api/WalletController";
import { AssetButton } from "@components/common/AssetButton";
import { Progress } from "@components/ui/progress";
import { cn } from "@/lib/utils";
import MemeSwapConfirm from "@components/shared/modals/Swap/MemeSwapConfirm";
import MemeSwapList from "./components/MemeSwapList";

const MemeSwap: FC = () => {
    const modalHelpers = createModalHelpers();
    const { openModal } = modalHelpers;
    const { isConnected, currentWallet } = useWallet();
    const isMnemonic = useWallet((s) => s.isMnemonic());
    // --- Token Info State ---
    const [mempadTokenInfo, setMempadTokenInfo] = useState<{
        price: string;
        priceInTon: number | null;
        marketCapInTon: number | null;
        rate: number | null;
    } | null>(null);
    const [isTokenInfoLoading, setIsTokenInfoLoading] = useState(false);
    const [tokenInfoError, setTokenInfoError] = useState<string | null>(null);
    const MemoizedSwapDetails = React.memo(SwapDetails);

    // Always destructure from useMemeSwap() for reactivity
    const {
        assetOutInfo,
        assetInInfo,
        setMemeSwapAsset,
        setAssetInAmount, // Fixed: using the correct function name from the store
        memeSwapTargetAddress,
        setMemeSwapTargetAddress,
        updateMemeSwapAssetBalance,
        setPad,
        pad,
        executeSwap,
        isMeme,
        setIsMeme,
        updateAllMemeSwapBalances,
    } = useMemeSwap();

    const [rawRate, setRawRate] = useState<number | undefined>(undefined);
    const formattedRate = useLowRegisterDecimal(rawRate, 12);

    // Sync walletGuid to MemeSwapList store for list fetching
    const setWalletGuid = useMemeSwapListStore((s) => s.setWalletGuid);
    useEffect(() => {
        if (currentWallet?.guid) {
            setWalletGuid(currentWallet.guid);
        }
    }, [currentWallet?.guid, setWalletGuid]);

    // Update balances when input/output asset or wallet changes
    useEffect(() => {
        if (isConnected && assetInInfo.asset && assetOutInfo.asset && currentWallet?.guid) {
            updateAllMemeSwapBalances();
        }
    }, [isConnected, assetInInfo.asset, assetOutInfo.asset, currentWallet?.guid]);

    // Sync assetInInfo.price with mempadTokenInfo.priceInTon
    useEffect(() => {
        if (mempadTokenInfo?.price) {
            useMemeSwap.setState((state) => ({
                assetOutInfo: {
                    ...state.assetOutInfo,
                    price: Number(mempadTokenInfo.price),
                },
            }));
            console.log("[MemeSwap] assetOutInfo.price updated to:", mempadTokenInfo.price);
        }
    }, [mempadTokenInfo?.price]);

    // Get pools and fetchLiquidityPools from the usePools hook
    const { pools, fetchLiquidityPools } = usePools();
    
    // Fetch pool info when assetOutInfo.asset or assetInInfo.asset changes
    useEffect(() => {
        const fetchPools = async () => {
            if (!assetOutInfo.asset || !assetOutInfo.asset.jettonMasterAddress) {
                return;
            }
            
            if (!assetInInfo.asset || !assetInInfo.asset.jettonMasterAddress) {
                return;
            }
            
            // Check if input and output assets are the same
            if (assetInInfo.asset.jettonMasterAddress === assetOutInfo.asset.jettonMasterAddress) {
                console.log("[fetchPools] Skipping pool fetch - input and output assets are the same");
                setIsMeme(true); // Treat as a meme token when same asset
                return;
            }
            
            try {
                console.log("[fetchPools] Fetching pools for:", {
                    assetIn: assetInInfo.asset.symbol,
                    assetInAddress: assetInInfo.asset.jettonMasterAddress,
                    assetOut: assetOutInfo.asset.symbol,
                    assetOutAddress: assetOutInfo.asset.jettonMasterAddress
                });
                
                await fetchLiquidityPools(assetOutInfo.asset, assetInInfo.asset, "1");
                // pools will be updated automatically through the hook
                setIsMeme(pools.length === 0);
                console.log("[fetchPools] isMeme:", pools.length === 0, pools);
            } catch (err) {
                console.error("[fetchPools] Error fetching pools:", err);
                // Set isMeme to true on error to provide a fallback behavior
                setIsMeme(true);
            }
        };
        fetchPools();
    }, [assetOutInfo.asset?.jettonMasterAddress, assetInInfo.asset?.jettonMasterAddress, fetchLiquidityPools, pools]);

    // Fetch token info when assetOutInfo.asset changes
    useEffect(() => {
        const fetchTokenInfo = async () => {
            if (!assetOutInfo.asset || !assetOutInfo.asset.jettonMasterAddress) {
                setMempadTokenInfo(null);
                setTokenInfoError(null);
                return;
            }
            setIsTokenInfoLoading(true);
            setTokenInfoError(null);
            try {
                const assetOutGuid = assetOutInfo.asset?.guid;
                if (!assetOutGuid) {
                    setMempadTokenInfo(null);
                    setTokenInfoError("No output asset selected");
                    return;
                }
                const resp = await WalletController.getMempadTokenInfo(assetOutGuid);
                if (resp && resp.data) {
                    const rawPrice = resp.data.priceInTon ?? "";
                    const numericRate =
                        rawPrice && !isNaN(Number(rawPrice)) && Number(rawPrice) > 0 ? 1 / Number(rawPrice) : undefined;
                    setRawRate(numericRate);
                    setMempadTokenInfo({
                        price: rawPrice,
                        priceInTon:
                            resp.data.priceInTon && !isNaN(Number(resp.data.priceInTon))
                                ? Number(resp.data.priceInTon)
                                : null,
                        marketCapInTon:
                            resp.data.marketCapInTon && !isNaN(Number(resp.data.marketCapInTon))
                                ? Number(resp.data.marketCapInTon)
                                : null,
                        rate: numericRate !== undefined ? numericRate : null,
                    });
                } else {
                    setMempadTokenInfo(null);
                    setTokenInfoError("No token info found");
                }
            } catch (err: any) {
                setMempadTokenInfo(null);
                setTokenInfoError(err?.message || "Failed to fetch token info");
            } finally {
                setIsTokenInfoLoading(false);
            }
        };
        fetchTokenInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetOutInfo.asset?.jettonMasterAddress]);
    const { fetchSnipes, snipes, isLoading } = useSnipesStore(); // Get snipes state and fetch action

    // --- Ensure TON balance is fetched on first load ---
    useEffect(() => {
        if (isConnected && currentWallet?.guid && assetOutInfo.asset) {
            updateMemeSwapAssetBalance();
        }
    }, [isConnected, currentWallet?.guid, assetOutInfo.asset]);

    const handleSetMemeSwapCurrency = () => {
        openModal("currencies");
    };

    const handleAddressChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        setMemeSwapTargetAddress(event.target.value);
    }, []);

    // Handle amount change
    const handleAmountChange = (value: string) => {
        setAssetInAmount(value);
    };

    const padOptions = [
        { value: "blumpump", label: "Blumpump" },
        { value: "gaspump", label: "Gaspump" },
    ];

    const handlePadChange = (pad: string) => {
        setPad(pad);
    };

    // Fetch snipes when the component mounts and the wallet is connected
    useEffect(() => {
        if (!isConnected) return;
        // Poll snipes every 15 seconds to reduce backend/API load
        const interval = setInterval(() => {
            fetchSnipes();
        }, 15000); // 15 seconds to reduce backend/API load
        return () => clearInterval(interval);
    }, [isConnected, fetchSnipes]);

    // Only show loading spinner if loading and snipes are empty
    const showReloading = isLoading && snipes.length === 0;
    // Check if the amount is valid for the snipe button
    const isAmountValid =
        assetOutInfo.asset?.symbol !== "TON" ||
        Number.parseFloat(assetOutInfo.amount) >= 0.5 ||
        isNaN(Number.parseFloat(assetOutInfo.amount));

    return (
        <Content>
            <Currencies setAsset={setMemeSwapAsset} variant="Buy" />
            <Card className="h-fit max-h-fit">
                <CardHeader
                    handleReload={() => {
                        fetchSnipes();
                    }}
                    handleSettingsModal={() => openModal("Settings")}
                    isLoading={showReloading} // Pass as a prop if CardHeader supports it
                />
                <Separator />
                <div className="flex flex-col gap-0 card p-0.5 rounded-[1.7rem]">
                    <div className="relative flex items-center w-full overflow-hidden rounded-full">
                        <AddressInput
                            value={memeSwapTargetAddress}
                            onChange={handleAddressChange}
                            placeholder="EQAAA...AM9C"
                            name="targetAddress"
                            required
                            asset={assetOutInfo.asset ?? undefined}
                            className="w-full"
                            error={isMeme ? undefined : { type: "manual", message: "Address is not a meme" }}
                        />
                        {assetOutInfo.asset && (
                            <AssetButton
                                asset={assetOutInfo.asset}
                                seletable={false}
                                onClick={() => {}}
                                className={cn(
                                    "absolute right-1.5 z-10 transition-transform duration-300 ease-in-out",
                                    !memeSwapTargetAddress && "translate-x-24",
                                )}
                                error={!isMeme}
                            />
                        )}
                    </div>
                    <div className="flex flex-col p-3 gap-2 py-3">
                        <div className="flex flex-row gap-4 justify-between w-full">
                            <div className="flex gap-1 items-center  w-fit">
                                <div className="text-sm">Price</div>
                                <div className="text-sm">
                                    {isTokenInfoLoading ? (
                                        <span className="text-gray-400">...</span>
                                    ) : tokenInfoError ? (
                                        <span className="text-red-500">-</span>
                                    ) : mempadTokenInfo?.priceInTon ? (
                                        <span>{formatRepeatingZeroDecimal(mempadTokenInfo.priceInTon, 14, 4)}</span>
                                    ) : (
                                        "-"
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-1 items-center w-fit">
                                <div className={"text-sm"}>MCap</div>
                                <div className="text-sm">
                                    {isTokenInfoLoading ? (
                                        <span className="text-gray-400">...</span>
                                    ) : tokenInfoError ? (
                                        <span className="text-red-500">-</span>
                                    ) : mempadTokenInfo?.marketCapInTon ? (
                                        <span>{formatRepeatingZeroDecimal(mempadTokenInfo.marketCapInTon, 14, 1)}</span>
                                    ) : (
                                        "-"
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 w-full justify-start">
                            <span className="w-fit flex whitespace-nowrap text-sm">Before listing</span>
                            <Progress
                                value={
                                    typeof mempadTokenInfo?.marketCapInTon === "number"
                                        ? mempadTokenInfo.marketCapInTon
                                        : 0
                                }
                                className=" h-2.5"
                            />
                            <span className="w-fit flex text-sm">
                                {typeof mempadTokenInfo?.marketCapInTon === "number"
                                    ? formatRepeatingZeroDecimal(mempadTokenInfo.marketCapInTon, 14, 1)
                                    : 0}
                                /1500
                            </span>
                        </div>{" "}
                    </div>
                </div>

                <CurrencyForm
                    title="Buy"
                    assetButton={true}
                    asset={assetInInfo && assetInInfo.asset}
                    amount={assetInInfo.amount}
                    setAmount={handleAmountChange}
                    balance={
                        isConnected && typeof assetInInfo.balance === "number"
                            ? assetInInfo.balance.toFixed(assetInInfo.balance < 0.0001 ? 8 : 4)
                            : "connect wallet"
                    }
                    handleSetCurrency={handleSetMemeSwapCurrency}
                    price={assetInInfo.price ?? undefined}
                    seletable={false}
                    right={true}
                    className="px-4"
                />

                <BigSwitch options={padOptions} value={pad} onValueChange={(value) => handlePadChange(value)} />
                <MemoizedSwapDetails
                    sellAssetInfo={assetInInfo}
                    buyAssetInfo={assetOutInfo}
                    minReceiveValue={"0"}
                    price={Number(assetOutInfo.price)}
                />
                {/* Show price and how much can be bought for 1 TON */}

                <MemeSwapButton
                    createMemeSwap={async () => {
                        openModal("memeSwap");
                    }}
                    isMnemonic={isMnemonic}
                    noAddress={memeSwapTargetAddress === ""}
                    insufficientBalance={Number(assetInInfo.amount) > assetInInfo.balance}
                    noSendAmount={Number(assetInInfo.amount) === 0}
                    invalidAmount={!isAmountValid}
                    onRequireMnemonicWallet={() => openModal("select")}
                    isMeme={isMeme}
                />
            </Card>
            {/* <SnipeList showMemes showHeader={false} twoCol={true} className="w-full col-span-2" /> */}
            <MemeSwapList twoCol={true} className="w-full col-span-2" />

            {useModalStore((s) => s.modalStates.memeSwap) && (
                <MemeSwapConfirm assetInInfo={assetInInfo} assetOutInfo={assetOutInfo} title="Confirm Swap">
                    <MemoizedSwapDetails
                        sellAssetInfo={assetInInfo}
                        buyAssetInfo={assetOutInfo}
                        minReceiveValue={"0"}
                        price={assetOutInfo.price}
                    />
                    <MemeSwapButton
                        createMemeSwap={async () => {
                            try {
                                const result = await executeSwap("internal");
                                const { showToast } = await import("@/utils/showToast");
                                if (result && result.status !== "error") {
                                    showToast("Swap created successfully!", "success");
                                    updateAllMemeSwapBalances();
                                } else {
                                    const result = await executeSwap("internal");
                                    const { showToast } = await import("@/utils/showToast");
                                    if (result && result.status !== "error") {
                                        showToast("Swap created successfully!", "success");
                                    } else {
                                        showToast(result?.message || "Failed to create swap", "error");
                                    }
                                }
                            } catch (error: any) {
                                const { showToast } = await import("@/utils/showToast");
                                showToast(error?.message || "Failed to create swap", "error");
                            }
                        }}
                        isMnemonic={isMnemonic}
                        noAddress={memeSwapTargetAddress === ""}
                        insufficientBalance={Number(assetInInfo.amount) > assetInInfo.balance}
                        noSendAmount={Number(assetInInfo.amount) === 0}
                        invalidAmount={!isAmountValid}
                        onRequireMnemonicWallet={() => openModal("select")}
                        isMeme={isMeme}
                    />
                </MemeSwapConfirm>
            )}
        </Content>
    );
};

export default MemeSwap;
